import localforage from "localforage";
import * as search from "./searchDb";
import toString from "../../utils/toString";
import storage from "../../utils/storage";
import config from "../config/config";
import { createLogger } from "../../utils/log";
import { gistTypes } from "./github";

const log = createLogger("db");
const tenant = storage.getTenant();

const storeNames = [
  `note-config`,
  `notes`,
  `note-content`,
  `note-content-versions`,
];
const schema = config.getSchemaVersion();

log.info("schema version ", schema);
if (schema === 1) {
  storeNames.forEach((name) => localforage.dropInstance({ name }));
  log.info("migrating from schema v1");
  config.incrementSchemaVersion();
  log.info("new schema version ", schema);
}

const configStore = localforage.createInstance({
  name: `${tenant}|note-config`,
});

const notesStore = localforage.createInstance({
  name: `${tenant}|notes`,
});

const noteContentStore = localforage.createInstance({
  name: `${tenant}|note-content`,
});

const noteContentVersionsStore = localforage.createInstance({
  name: `${tenant}|note-content-versions`,
});

let searchIndexInitialised = false;

export const iterateNoteContents =
  noteContentStore.iterate.bind(noteContentStore);

export async function setNotes(...notes) {
  const promises = notes.map((n) => notesStore.setItem(n.id, n));

  await Promise.all(promises);
}

export async function patchNoteContent(id, { deleted, tags, title }) {
  const content = await noteContentStore.getItem(id);
  if (content) {
    if (deleted != null) content.deleted = deleted;
    if (tags != null) content.tags = tags;
    if (title != null) content.title = title;
    await noteContentStore.setItem(id, content);
    if (deleted) search.remove(id);
    else search.set(id, content.data);
  }
}

export async function deleteOrphanNotes(ids) {
  const notesToDelete = [];
  await notesStore.iterate((note, key) => {
    const exists = ids[key];
    if (!exists) {
      notesToDelete.push(note);
    }
  });
  for (let note of notesToDelete) {
    log.info("Deleting orphan note", note);
    await deleteNote(note.id);
  }
  return notesToDelete;
}

export async function setNoteContent(note, data) {
  const contentWithMeta = {
    id: note.id,
    data,
    updated_at: note.updated_at,
    deleted: note.deleted,
    tags: note.tags,
    title: note.title,
  };
  await noteContentStore.setItem(note.id, contentWithMeta);
  await noteContentVersionsStore.setItem(
    generateVersionKey(contentWithMeta),
    true
  );
  if (note.deleted) {
    search.remove(note.id);
  } else {
    search.set(note.id, contentWithMeta.data);
  }
  return contentWithMeta;
}

export async function hasNoteContent(note) {
  const key = generateVersionKey(note);
  const exists =
    (await noteContentVersionsStore.getItem(key)) &&
    (await noteContentStore.getItem(note.id));
  return !!exists;
}

export function generateVersionKey({ id, updated_at }) {
  // we do this so we can delete all key versions with prefix
  if (updated_at == null) updated_at = "";
  return `${id}|${updated_at}`;
}

export async function getAllNotes() {
  let batch = [];
  await notesStore.iterate((note) => {
    // Shared notes could get into the db because
    // of old and new versions running at the same time.
    if (!note.type || note.type === gistTypes.note) batch.push(note);
  });

  return batch;
}

export async function getDeletedNotes() {
  let batch = [];
  await notesStore.iterate((note) => {
    // Shared notes could get into the db because
    // of old and new versions running at the same time.
    const isNoteType = !note.type || note.type === gistTypes.note;

    if (isNoteType && note.deleted) batch.push(note);
  });

  return batch;
}

export async function getNoteById(id) {
  return await notesStore.getItem(id);
}

export async function deleteNote(id) {
  await noteContentStore.removeItem(id);
  await notesStore.removeItem(id);
  const keys = await noteContentVersionsStore.keys();
  const deleteKeyPrefix = generateVersionKey(id);
  for (let key of keys) {
    if (key.startsWith(deleteKeyPrefix)) {
      await noteContentVersionsStore.removeItem(key);
    }
  }
  search.remove(id);
}

export async function getLastUpdatedTimestamp() {
  return await configStore.getItem("lastUpdatedTimestamp");
}

export async function setLastUpdatedTimestamp(timestamp) {
  await configStore.setItem("lastUpdatedTimestamp", timestamp);
}
